/**
 * Product Size Option Translation
 *
 * Dynamically translates product option values (like Size) based on the selected unit
 * (Age, Centimeter, Inches). Uses data from Shopify metaobjects to provide localized
 * size representations.
 *
 * @author Shopify Theme Developer
 * @version 1.0.0
 */

/**
 * @typedef {Object} MeasurementTableRow
 * @property {string} label - The label for the measurement row
 * @property {string[]} values - Array of values [Age, Centimeter, Inches]
 */

/**
 * @typedef {Object} SizeGuideData
 * @property {Object} main_size_guide - Main size guide configuration
 * @property {MeasurementTableRow[]} measurement_table_rows - Array of measurement data
 */

/**
 * @typedef {Object} UnitSelector
 * @property {HTMLElement} element - The DOM element
 * @property {string} unit - The unit type (Age, Centimeter, Inches)
 * @property {number} index - The index in measurement_table_rows values array
 */

class ProductSizeTranslator {
  /**
   * Initialize the size translation system
   */
  constructor() {
    /** @type {SizeGuideData|null} */
    this.sizeGuideData = null;

    /** @type {UnitSelector[]} */
    this.unitSelectors = [];

    /** @type {HTMLElement[]} */
    this.sizeOptions = [];

    /** @type {Map<string, string>} */
    this.originalValues = new Map();

    /** @type {string} */
    this.currentUnit = 'Age'; // Default unit

    /** @type {number} */
    this.currentUnitIndex = 0; // Default to Age (index 0)

    this.init();
  }

  /**
   * Initialize the translation system
   */
  init() {
    try {
      this.loadSizeGuideData();
      this.findSizeOptions();
      this.setupUnitSelectors();
      this.storeOriginalValues();
      this.bindEvents();
    } catch (error) {
      // Initialization failed - fail silently in production
    }
  }

  /**
   * Load size guide data from the JSON script tag
   * @throws {Error} If JSON data is not found or invalid
   */
  loadSizeGuideData() {
    const scriptTag = document.querySelector('script[data-size-data]');

    if (!scriptTag) {
      throw new Error('Size guide JSON data not found');
    }

    try {
      this.sizeGuideData = JSON.parse(scriptTag.textContent);

      // Check if measurement_table_rows exists and has valid data structure
      if (
        !this.sizeGuideData.measurement_table_rows ||
        this.sizeGuideData.measurement_table_rows.length === 0
      ) {
        throw new Error('measurement_table_rows not found in data');
      }

      // Validate that measurement_table_rows has the expected structure (not GIDs)
      const firstRow = this.sizeGuideData.measurement_table_rows[0];
      if (
        !firstRow ||
        !firstRow.label ||
        !firstRow.values ||
        !Array.isArray(firstRow.values)
      ) {
        throw new Error(
          'measurement_table_rows has invalid structure - needs readable data, not GIDs'
        );
      }
    } catch (parseError) {
      throw new Error(`Failed to parse size guide JSON: ${parseError.message}`);
    }
  }

  /**
   * Find all size option elements in the DOM
   */
  findSizeOptions() {
    // Find all product option containers
    const optionContainers = document.querySelectorAll(
      '[data-option-position]'
    );

    optionContainers.forEach((container) => {
      const optionNameElement = container.querySelector(
        '.radio__legend__option-name'
      );

      if (optionNameElement) {
        const optionName = optionNameElement.textContent.trim();

        // Check if this is a size option (case-insensitive)
        if (
          optionName.toLowerCase().includes('size') ||
          optionName.toLowerCase().includes('størrelse')
        ) {
          // Find all option selectors within this container
          const optionSelectors = container.querySelectorAll(
            '[data-single-option-selector]'
          );
          optionSelectors.forEach((selector) => {
            this.sizeOptions.push(selector);
          });

          // Also find radio button labels
          const radioLabels = container.querySelectorAll('label span');
          radioLabels.forEach((label) => {
            if (
              label.textContent.trim() &&
              !label.classList.contains('visually-hidden')
            ) {
              this.sizeOptions.push(label);
            }
          });

          // Find select option elements
          const selectOptions = container.querySelectorAll(
            '.select-popout__option span'
          );
          selectOptions.forEach((option) => this.sizeOptions.push(option));
        }
      }
    });
  }

  /**
   * Find the index of a unit in the measurement table values array
   * @param {string} unit - The unit to find
   * @returns {number} The index in the measurement table values array, or 0 if not found
   */
  findUnitIndexInMeasurementTable(unit) {
    if (
      !this.sizeGuideData ||
      !this.sizeGuideData.measurement_table_rows ||
      this.sizeGuideData.measurement_table_rows.length === 0
    ) {
      return 0;
    }

    // Get the first row which contains the unit headers
    const firstRow = this.sizeGuideData.measurement_table_rows[0];
    if (!firstRow || !firstRow.values || !Array.isArray(firstRow.values)) {
      return 0;
    }

    // Find the index of the unit in the values array
    const unitIndex = firstRow.values.findIndex(
      (value) => value.toLowerCase().trim() === unit.toLowerCase().trim()
    );

    // Return the found index, or 0 if not found (fallback to first unit)
    return unitIndex >= 0 ? unitIndex : 0;
  }

  /**
   * Setup unit selector elements and determine current selection
   */
  setupUnitSelectors() {
    const unitContainer = document.querySelector('[data-size-unit-selectors]');

    if (!unitContainer) {
      return;
    }

    const selectorElements = unitContainer.querySelectorAll(
      '[data-size-unit-selector]'
    );
    const unitOfMeasurement =
      this.sizeGuideData.unit_of_measurement?.toLowerCase();

    selectorElements.forEach((element) => {
      const unit = element.getAttribute('data-size-unit-selector');
      const unitLower = unit.toLowerCase();

      // Always show Age, but filter Centimeter/Inches based on unit of measurement
      let shouldShow = true;
      if (unitLower.includes('centimeter') || unitLower.includes('cm')) {
        // Show centimeter only for metric
        shouldShow = unitOfMeasurement === 'metric';
      } else if (unitLower.includes('inch') || unitLower.includes('in')) {
        // Show inches only for imperial
        shouldShow = unitOfMeasurement === 'imperial';
      }
      // Age is always shown (shouldShow remains true)

      if (shouldShow) {
        element.style.display = ''; // eslint-disable-line no-param-reassign

        // Find the correct index in the measurement table values array
        const measurementTableIndex =
          this.findUnitIndexInMeasurementTable(unit);

        this.unitSelectors.push({
          element,
          unit,
          index: measurementTableIndex,
        });

        // Check if this is the currently selected unit
        if (element.classList.contains('selected')) {
          this.currentUnit = unit;
          this.currentUnitIndex = measurementTableIndex;
        }
      } else {
        // Hide the unit selector
        element.style.display = 'none'; // eslint-disable-line no-param-reassign
      }
    });

    // If no unit is selected (maybe the selected one was hidden), select the first visible one
    if (this.unitSelectors.length > 0 && !this.currentUnit) {
      const firstSelector = this.unitSelectors[0];
      firstSelector.element.classList.add('selected');
      this.currentUnit = firstSelector.unit;
      this.currentUnitIndex = firstSelector.index;
    }
  }

  /**
   * Store original values for restoration and mapping
   */
  storeOriginalValues() {
    this.sizeOptions.forEach((element) => {
      const originalValue = ProductSizeTranslator.getElementValue(element);
      if (originalValue) {
        this.originalValues.set(element, originalValue);
      }
    });
  }

  /**
   * Get the text value from different types of elements
   * @param {HTMLElement} element - The element to get value from
   * @returns {string} The element's text value
   */
  static getElementValue(element) {
    if (element.tagName === 'INPUT') {
      return element.value;
    }
    if (element.tagName === 'OPTION') {
      return element.textContent.trim();
    }
    return element.textContent.trim();
  }

  /**
   * Set the text value for different types of elements
   * @param {HTMLElement} element - The element to update
   * @param {string} value - The new value to set
   */
  static setElementValue(element, value) {
    if (element.tagName === 'INPUT') {
      element.value = value; // eslint-disable-line no-param-reassign
    } else {
      element.textContent = value; // eslint-disable-line no-param-reassign
    }
  }

  /**
   * Bind event listeners to unit selectors
   */
  bindEvents() {
    if (this.unitSelectors.length === 0) {
      return;
    }

    this.unitSelectors.forEach((unitSelector) => {
      unitSelector.element.addEventListener('click', (event) => {
        event.preventDefault();
        this.handleUnitChange(unitSelector);
      });
    });
  }

  /**
   * Handle unit selector change
   * @param {UnitSelector} selectedUnit - The selected unit object
   */
  handleUnitChange(selectedUnit) {
    // Update visual selection
    this.unitSelectors.forEach((unitSelector) => {
      unitSelector.element.classList.remove('selected');
    });
    selectedUnit.element.classList.add('selected');

    // Update current unit and find the correct measurement table index
    this.currentUnit = selectedUnit.unit;
    this.currentUnitIndex = this.findUnitIndexInMeasurementTable(
      selectedUnit.unit
    );

    // Translate all size options
    this.translateSizeOptions();
  }

  /**
   * Translate all size option values based on current unit
   */
  translateSizeOptions() {
    if (!this.sizeGuideData || !this.sizeGuideData.measurement_table_rows) {
      return;
    }

    this.sizeOptions.forEach((element) => {
      const originalValue = this.originalValues.get(element);

      if (originalValue) {
        const translatedValue = this.getTranslatedValue(originalValue);
        if (translatedValue) {
          // Always apply the translation, even if it matches the original
          element.textContent = translatedValue; // eslint-disable-line no-param-reassign
        }
      }
    });
  }

  /**
   * Check if a row contains the target value
   * @param {Object} row - The measurement row
   * @param {string} targetValue - The value to search for
   * @returns {boolean} True if row contains the value
   */
  static rowContainsValue(row, targetValue) {
    return row.values.some(
      (value) => value.toLowerCase().trim() === targetValue
    );
  }

  /**
   * Get translated value for a given original value
   * @param {string} originalValue - The original size value
   * @returns {string|null} The translated value or null if not found
   */
  getTranslatedValue(originalValue) {
    const measurementRows = this.sizeGuideData.measurement_table_rows;

    // Find the row that contains the original value
    const lowerOriginal = originalValue.toLowerCase().trim();
    const isMatchingRow = (row) =>
      ProductSizeTranslator.rowContainsValue(row, lowerOriginal);
    const matchingRow = measurementRows.find(isMatchingRow);

    if (matchingRow && matchingRow.values[this.currentUnitIndex]) {
      return matchingRow.values[this.currentUnitIndex];
    }

    return null;
  }

  /**
   * Reset all size options to their original values
   */
  resetToOriginalValues() {
    this.originalValues.forEach((originalValue, element) => {
      // ProductSizeTranslator.setElementValue(element, originalValue);
      element.textContent = originalValue; // eslint-disable-line no-param-reassign
    });
  }

  /**
   * Get current translation state
   * @returns {Object} Current state information
   */
  getState() {
    return {
      currentUnit: this.currentUnit,
      currentUnitIndex: this.currentUnitIndex,
      totalSizeOptions: this.sizeOptions.length,
      totalUnitSelectors: this.unitSelectors.length,
      hasData: !!this.sizeGuideData,
    };
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Only initialize if we have the required elements
  const unitContainer = document.querySelector('[data-size-unit-selectors]');
  const jsonScript = document.querySelector('script[data-size-data]');

  if (unitContainer && jsonScript) {
    window.productSizeTranslator = new ProductSizeTranslator();
  }
});

// Export for potential external usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductSizeTranslator;
}
